import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Type, Server, Cpu, Volume2, Plug, FileStack, Mic, Languages } from "lucide-react";

interface SettingsCategory {
  href: string;
  label: string;
  description: string;
  icon: typeof Type;
}

// Mirrors AgentForm.tsx's own field groupings ("Identity" -> typeId,
// "Model & Provider" -> modelId/providerId/voiceId) - these four categories
// each populate one selectable field on Agent.
const AGENT_CONFIGURATION_CATEGORIES: SettingsCategory[] = [
  {
    href: "/dashboard/settings/agentTypes",
    label: "Agent Types",
    description:
      "The categories of agents (e.g. chat vs. voice) available when creating an agent - each option here populates an agent's typeId field.",
    icon: Type,
  },
  {
    href: "/dashboard/settings/models",
    label: "Models",
    description:
      "The LLM models available to agents, including the exact model identifier sent to the provider API - each option here populates an agent's modelId field.",
    icon: Cpu,
  },
  {
    href: "/dashboard/settings/providers",
    label: "Providers",
    description:
      "The telephony/voice providers agents can use (e.g. Twilio) and their TwiML behavior - each option here populates an agent's providerId field.",
    icon: Server,
  },
  {
    href: "/dashboard/settings/voices",
    label: "Voices",
    description:
      "The TTS voices agents can speak with, including provider-specific voice identifiers - each option here populates an agent's voiceId field.",
    icon: Volume2,
  },
  {
    href: "/dashboard/settings/transcriptionProviders",
    label: "Transcription Providers",
    description:
      "The speech-to-text providers voice agents can use during calls - each option here populates a voice agent's transcriptionProvider field.",
    icon: Mic,
  },
  {
    href: "/dashboard/settings/languages",
    label: "Languages",
    description:
      "The speech languages/locales voice agents can use during calls - each option here populates a voice agent's language field.",
    icon: Languages,
  },
  {
    href: "/dashboard/agent-blueprints",
    label: "Agent Blueprints",
    description:
      "Reusable agent configuration templates (system prompt, model, voice, scheduling defaults) that pre-fill AgentForm when deploying a new agent.",
    icon: FileStack,
  },
];

const INTEGRATION_CATEGORIES: SettingsCategory[] = [
  {
    href: "/dashboard/integration-types",
    label: "Integrations",
    description:
      "The catalog of integration types clients can install (e.g. Calendly, GoHighLevel) - each option here populates an installed Integration's typeId field.",
    icon: Plug,
  },
];

export const Route = createFileRoute("/dashboard/settings/")({
  component: RouteComponent,
});

function CategoryGrid({ categories }: { categories: SettingsCategory[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {categories.map((category) => {
        const Icon = category.icon;
        return (
          <Link key={category.href} to={category.href}>
            <Card className="hover:bg-accent transition-colors cursor-pointer h-full">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Icon className="w-6 h-6 text-muted-foreground" />
                  <CardTitle>{category.label}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {category.description}
                </p>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

function RouteComponent() {
  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Settings</h1>
          <p className="text-lg text-muted-foreground">
            Manage form options and configuration.
          </p>
        </div>

        <div className="space-y-10">
          <div>
            <h2 className="text-xl font-semibold mb-4">Agent Configuration</h2>
            <CategoryGrid categories={AGENT_CONFIGURATION_CATEGORIES} />
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Integrations</h2>
            <CategoryGrid categories={INTEGRATION_CATEGORIES} />
          </div>
        </div>
      </div>
    </div>
  );
}
